import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Check, ChevronDown } from 'lucide-react';
import styles from './Select.module.scss';
import {
  LIST_MAX_HEIGHT,
  appendTypeAheadKey,
  computePopupPlacement,
  getViewportHeight,
  initialActiveIndex,
  isOptionEnabled,
  isTypeAheadKey,
  lastEnabledIndex,
  firstEnabledIndex,
  measureNaturalPopupHeight,
  nextActiveIndex,
  samePlacement,
  scrollOptionIntoView,
  typeAheadIndex,
} from './listbox.helpers';

/* ── Select ─────────────────────────────────────────────────────
 * Single-choice dropdown. The trigger and opened option panel
 * share visual language with MultiSelect / SearchableSelect (soft
 * blue selected row, trailing check, portal popup that escapes
 * ancestor overflow), but the public API stays children-based —
 *   <Select value={x} onChange={e => ...} name="...">
 *     <option value="">Placeholder</option>
 *     <option value="a">A</option>
 *   </Select>
 * so the 60+ existing callers that read `e.target.value` keep
 * working without changes. We synthesize an event-shaped object
 * on selection.
 *
 * Keyboard (WAI-ARIA "select-only combobox"): focus never leaves
 * the trigger. While the list is open the trigger's
 * aria-activedescendant names the highlighted option, which is how
 * a screen reader knows what the arrows landed on.
 *   closed:  ArrowDown / ArrowUp / Enter / Space open on the current
 *            value, Home / End open on the first / last option
 *   open:    ArrowDown / ArrowUp / Home / End move the highlight
 *            (skipping disabled heading rows), Enter / Space pick it,
 *            Escape closes, Tab closes and lets focus move on.
 *   typing:  a letter or digit jumps the highlight to the next row
 *            starting with it, and keys pressed in quick succession
 *            spell out a longer search ("exp" → Express). See
 *            listbox.helpers' type-to-select section for the rules.
 * ────────────────────────────────────────────────────────────── */

const OPEN_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'];

const flattenChildren = (children) => {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      out.push(...flattenChildren(child.props.children));
    } else {
      out.push(child);
    }
  });
  return out;
};

const parseOptions = (children) => {
  return flattenChildren(children)
    .filter((c) => c.type === 'option')
    .map((c) => {
      const rawLabel = c.props.children;
      const label =
        typeof rawLabel === 'string' || typeof rawLabel === 'number'
          ? String(rawLabel)
          : Array.isArray(rawLabel)
          ? rawLabel.join('')
          : '';
      // Mirror native `<option>` behaviour: HTML attributes are always
      // strings, so a native <select>'s `e.target.value` is always a
      // string regardless of what you passed to `value={…}` (e.g. an
      // Array becomes "a,b,c", a number becomes "42"). Callers written
      // against the native select rely on that — Leave.js for example
      // does `compoundValue.split(',')` on the value from a
      // `<option value={Object.values(item)}>`. Coerce here so onChange
      // delivers a string just like the native control would.
      const rawValue = c.props.value;
      const value = rawValue == null ? '' : String(rawValue);
      return {
        value,
        label,
        disabled: !!c.props.disabled,
      };
    });
};

const Select = ({
  label,
  error,
  helperText,
  children,
  id,
  className,
  value,
  onChange,
  name,
  disabled,
  placeholder,
  required,
  // Pulled out of `rest` so a caller's own onKeyDown runs alongside
  // the keyboard support below instead of silently replacing it (the
  // `{...rest}` spread comes last on the trigger).
  onKeyDown,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [popupRect, setPopupRect] = useState(null);
  // Index into `options` of the highlighted row, -1 for none. This is
  // "where the arrow keys are", not the committed value — the value
  // only changes on Enter / Space / click.
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const listRef = useRef(null);
  // True while the highlight was last moved by the keyboard (or by
  // opening). Only then do we scroll the list to follow it: a mouse
  // hover already has its row under the pointer, and scrolling there
  // would slide a different row under a still pointer and make the
  // highlight creep.
  const scrollActiveRef = useRef(false);
  // What the user has typed in the last second or so, for type-to-
  // select. A ref, not state: it must never cause a re-render of its
  // own (the highlight moving is the only visible effect), and the
  // keydown handler needs the value that is current *right now*, not
  // the one captured when this render started.
  const typeAheadRef = useRef({ str: '', at: 0 });

  // The popup is a new DOM node on every open. Holding it in state (via
  // a callback ref) lets a layout effect re-measure the moment it
  // mounts — its natural height is unknown until then.
  const [popupEl, setPopupEl] = useState(null);
  const setPopupNode = useCallback((node) => {
    popupRef.current = node;
    setPopupEl(node);
  }, []);

  // useId, not the `id` prop: callers occasionally reuse an id, and a
  // duplicated listbox id would make aria-activedescendant ambiguous.
  const listboxId = `${useId()}-listbox`;
  const optionId = (index) => `${listboxId}-option-${index}`;

  const options = useMemo(() => parseOptions(children), [children]);

  // Treat an option with an empty-string value as a placeholder row,
  // matching the long-established `<option value="">Select X</option>`
  // convention used throughout the codebase.
  const selectedOption = useMemo(() => {
    if (value == null) return null;
    const match = options.find((o) => String(o.value) === String(value));
    if (!match) return null;
    if (match.value === '' || match.value === null) return null;
    return match;
  }, [value, options]);

  const placeholderText = useMemo(() => {
    if (placeholder) return placeholder;
    const empty = options.find((o) => o.value === '' || o.value === null);
    return empty?.label || 'Select…';
  }, [placeholder, options]);

  // Children can change while the list is open; never point
  // aria-activedescendant at a row that no longer exists.
  const activeIdx = activeIndex >= 0 && activeIndex < options.length ? activeIndex : -1;

  // Flip / clamp decision — see computePopupPlacement. Select's options
  // are static while it is open, so its natural height is exact and a
  // short list near the bottom of the page can stay below if it fits.
  // Before the popup has mounted we assume the tallest it can be (the
  // list's CSS cap plus borders); the mount effect below corrects it
  // before the browser paints.
  const updatePopupRect = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const next = computePopupPlacement({
      trigger: trigger.getBoundingClientRect(),
      viewportHeight: getViewportHeight(),
      popupHeight: measureNaturalPopupHeight(
        popupRef.current,
        listRef.current,
        LIST_MAX_HEIGHT + 2
      ),
    });
    // Returning `prev` bails out of the re-render — this runs on every
    // scroll event, so most calls should cost nothing.
    setPopupRect((prev) => (samePlacement(prev, next) ? prev : next));
  };

  const isSelectedOption = (o) =>
    !!selectedOption && String(o.value) === String(selectedOption.value);

  // `startIndex` is type-to-select's way in: the list opens with the row
  // the typed letters found highlighted, instead of the current value.
  const openMenu = (key, startIndex) => {
    let start = initialActiveIndex(options, isSelectedOption);
    if (key === 'Home') start = firstEnabledIndex(options);
    if (key === 'End') start = lastEnabledIndex(options);
    if (startIndex != null && startIndex >= 0) start = startIndex;
    scrollActiveRef.current = true;
    setActiveIndex(start);
    setOpen(true);
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    setOpen(false);
    // A closed list ends the typed word: reopening should start a fresh
    // search rather than continue one the user has forgotten about.
    typeAheadRef.current = { str: '', at: 0 };
    // preventScroll: the trigger is already on screen (the user just
    // used it); letting focus() scroll could nudge the page.
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      const inTrigger =
        wrapperRef.current && wrapperRef.current.contains(e.target);
      const inPopup = popupRef.current && popupRef.current.contains(e.target);
      if (!inTrigger && !inPopup) setOpen(false);
    };
    // Fallback for Escape pressed while focus is somewhere other than
    // the trigger. Escape on the trigger itself is handled (and
    // stopped) in handleKeyDown below.
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Lock popup to trigger rect; sync with scroll/resize so it tracks the
  // trigger even when an ancestor (PagePanel, drawer body) scrolls. The
  // flip / clamp decision is re-taken on every one of these, so a popup
  // that no longer fits after a scroll moves to the side that does.
  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePopupRect();
    const scrollParents = [];
    let node = triggerRef.current?.parentElement;
    while (node && node !== document.body) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollParents.push(node);
      }
      node = node.parentElement;
    }
    const handler = () => updatePopupRect();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    scrollParents.forEach((n) => n.addEventListener('scroll', handler));
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
      scrollParents.forEach((n) => n.removeEventListener('scroll', handler));
    };
    // updatePopupRect only reads refs, so the copy captured on open
    // stays correct for the whole time the popup is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Second pass, once the popup exists and its real height can be read.
  // Runs before paint, so the first frame the user sees is already on
  // the correct side.
  useLayoutEffect(() => {
    if (open && popupEl) updatePopupRect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, popupEl]);

  // Keep the keyboard highlight in view as it moves through a list
  // longer than the popup. popupRect is a dependency because the list's
  // visible height is only final once the flip / clamp pass has run;
  // scrolling is a no-op when the row is already visible.
  useLayoutEffect(() => {
    if (!open || !popupEl || activeIdx < 0 || !scrollActiveRef.current) return;
    scrollOptionIntoView(listRef.current, document.getElementById(optionId(activeIdx)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, popupEl, activeIdx, popupRect]);

  const pick = (optValue) => {
    if (onChange) {
      // Synthesize a native-select-style event so callers can keep
      // doing `e.target.value` / `e.target.name`.
      onChange({
        target: { name: name || '', value: optValue, type: 'select-one' },
        currentTarget: { name: name || '', value: optValue },
      });
    }
    // Back to the trigger so Tab carries on from this field. (Clicking
    // a row would otherwise leave focus on <body>.)
    closeMenu({ restoreFocus: true });
  };

  // Fold this key press into the typed search and report the row it
  // finds. `from` is the row to start looking AFTER — see typeAheadIndex.
  const searchByTyping = (key, from) => {
    typeAheadRef.current = appendTypeAheadKey(typeAheadRef.current, key, Date.now());
    return typeAheadIndex(options, typeAheadRef.current.str, from + 1);
  };

  // A modifier means the key belongs to the browser or the operating
  // system (Ctrl+F, Cmd+R), not to this list.
  const isTypingKey = (e) =>
    isTypeAheadKey(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey;

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
    if (disabled || e.defaultPrevented) return;

    if (!open) {
      if (OPEN_KEYS.includes(e.key)) {
        e.preventDefault();
        openMenu(e.key);
        return;
      }
      if (isTypingKey(e)) {
        // Typing on a closed field opens it on the match, so the user
        // can see what their letters did before committing to it. With
        // nothing to show for the keystroke we stay out of the way
        // entirely — no preventDefault — in case the page itself uses
        // that letter for something.
        const hit = searchByTyping(
          e.key,
          initialActiveIndex(options, isSelectedOption)
        );
        if (hit >= 0) {
          e.preventDefault();
          e.stopPropagation();
          openMenu(undefined, hit);
        }
      }
      return;
    }

    const next = nextActiveIndex(e.key, options, activeIdx);
    if (next !== null) {
      // preventDefault stops the page scrolling under the popup;
      // stopPropagation keeps a parent's own arrow handling (a grid,
      // a tab strip) from acting on the same key press.
      e.preventDefault();
      e.stopPropagation();
      scrollActiveRef.current = true;
      setActiveIndex(next);
      return;
    }

    // Typing, checked before the Enter / Space / Escape / Tab switch
    // below but after the arrows: none of those keys is a single
    // printable character, and the space bar is excluded by
    // isTypeAheadKey, so the two never compete for the same press.
    if (isTypingKey(e)) {
      // Swallowed either way: while the list is open the keystroke
      // belongs to it, and letting it through to a page-level shortcut
      // (or to the browser's own quick-find) behind an open dropdown
      // would be worse than doing nothing.
      e.preventDefault();
      e.stopPropagation();
      const hit = searchByTyping(e.key, activeIdx);
      if (hit >= 0) {
        scrollActiveRef.current = true;
        setActiveIndex(hit);
      }
      return;
    }

    switch (e.key) {
      case 'Enter':
      case ' ': {
        e.preventDefault();
        e.stopPropagation();
        const option = options[activeIdx];
        if (isOptionEnabled(option)) pick(option.value);
        break;
      }
      case 'Escape':
        // Stop here so the Escape closes only this list, not also the
        // modal or drawer the Select sits in.
        e.preventDefault();
        e.stopPropagation();
        closeMenu({ restoreFocus: true });
        break;
      case 'Tab':
        // No preventDefault: focus moves to the next field as usual.
        closeMenu();
        break;
      default:
        break;
    }
  };

  const triggerClasses = [
    styles.trigger,
    open ? styles.triggerOpen : '',
    error ? styles.triggerError : '',
    disabled ? styles.triggerDisabled : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  // NOTE: `className` lands on the TRIGGER above, not on this
  // wrapper (Input does the opposite). The wrapper is a column
  // flex, so a `flex`/`height` rule aimed at the trigger sizes the
  // control vertically. To size a Select inside a filter row, put
  // the rule on an element of your own around it.
  const wrapperClasses = [styles.wrapper].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} ref={wrapperRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && activeIdx >= 0 ? optionId(activeIdx) : undefined}
        aria-required={required || undefined}
        // The trigger is a <div>, not a real <select>, so the HTML
        // `disabled` attribute means nothing to it — a screen reader
        // would announce a perfectly ordinary combobox. aria-disabled is
        // how a custom control says "this exists, and it is switched
        // off": greyed out AND out of the tab order (tabIndex -1) with
        // nothing announced is a control that has silently vanished for
        // anyone not using a mouse.
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className={triggerClasses}
        onClick={() => {
          if (disabled) return;
          if (open) closeMenu();
          else openMenu();
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        <span className={styles.value}>
          {selectedOption ? (
            <span className={styles.valueLabel}>{selectedOption.label}</span>
          ) : (
            <span className={styles.placeholder}>{placeholderText}</span>
          )}
        </span>
        <span
          className={[styles.caret, open ? styles.caretOpen : '']
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        >
          <ChevronDown size={16} />
        </span>
      </div>

      {open && popupRect &&
        createPortal(
          <div
            ref={setPopupNode}
            className={styles.popup}
            data-placement={popupRect.placement}
            style={{
              position: 'fixed',
              // Exactly one of top / bottom is set: a popup flipped
              // above the trigger is anchored by its bottom edge.
              ...(popupRect.top != null ? { top: popupRect.top } : {}),
              ...(popupRect.bottom != null ? { bottom: popupRect.bottom } : {}),
              left: popupRect.left,
              width: popupRect.width,
              maxHeight: popupRect.maxHeight,
            }}
          >
            {options.length === 0 ? (
              <div className={styles.empty}>No options</div>
            ) : (
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                aria-label={label || rest['aria-label'] || undefined}
                className={styles.list}
              >
                {options.map((o, i) => {
                  const checked =
                    selectedOption &&
                    String(selectedOption.value) === String(o.value);
                  const isPlaceholderRow = o.value === '' || o.value === null;
                  const isActive = i === activeIdx;
                  const cls = [
                    styles.option,
                    checked ? styles.optionSelected : '',
                    isActive ? styles.optionActive : '',
                    o.disabled ? styles.optionDisabled : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  // No key handler on the rows: the trigger keeps focus the
                  // whole time and drives them via aria-activedescendant.
                  return (
                    <li
                      key={`${o.value}-${i}`}
                      id={optionId(i)}
                      role="option"
                      aria-selected={!!checked}
                      aria-disabled={o.disabled || undefined}
                      className={cls}
                      // Keep focus on the trigger when a row is pressed, so
                      // the keyboard keeps working if the click is dragged
                      // off the row and nothing gets picked.
                      onMouseDown={(e) => e.preventDefault()}
                      // mousemove, not mouseenter: scrolling the list with
                      // the keyboard slides rows under a still pointer and
                      // must not steal the highlight.
                      onMouseMove={() => {
                        if (o.disabled || i === activeIdx) return;
                        scrollActiveRef.current = false;
                        setActiveIndex(i);
                      }}
                      onClick={() => !o.disabled && pick(o.value)}
                    >
                      <span className={styles.optionBody}>
                        <div
                          className={
                            isPlaceholderRow
                              ? styles.optionPlaceholderLabel
                              : styles.optionLabel
                          }
                        >
                          {o.label}
                        </div>
                      </span>
                      {checked && (
                        <span className={styles.optionCheck} aria-hidden="true">
                          <Check size={14} strokeWidth={3} />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>,
          document.body
        )}

      {error && <span className={styles.error}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  error: PropTypes.string,
  helperText: PropTypes.string,
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  onKeyDown: PropTypes.func,
};

Select.defaultProps = {
  label: '',
  error: '',
  helperText: '',
  id: undefined,
  className: '',
  value: undefined,
  onChange: undefined,
  name: undefined,
  disabled: false,
  placeholder: '',
  required: false,
  onKeyDown: undefined,
};

export default Select;
